import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import { formatApiError } from '../lib/api';
import SEO from '../components/SEO';

export default function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(name, email, password);
      } else {
        await login(email, password);
      }
      navigate('/portal');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Hiba történt.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center bg-[#FAFAFA]">
      <SEO
        title={isRegister ? 'Regisztráció' : 'Bejelentkezés'}
        description="Jelentkezzen be az AGM Consulting ügyfélportáljára. Biztonságos hozzáférés egyenlegekhez, dokumentumokhoz és kimutatásokhoz."
      />
      <div className="w-full max-w-md px-4">
        <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-8 sm:p-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 bg-[#0B1B3D] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm font-['Outfit']">AGM</span>
              </div>
            </Link>
            <h1 className="mt-4 text-2xl font-['Outfit'] font-semibold text-[#0B1B3D]">
              {isRegister ? 'Regisztráció' : 'Bejelentkezés'}
            </h1>
            <p className="mt-2 text-sm text-[#64748B]">
              {isRegister ? 'Hozzon létre új fiókot' : 'Ügyfélportál hozzáférés'}
            </p>
          </div>

          {error && (
            <div data-testid="login-error" className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-[#1A1F2C] font-medium">Név</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  placeholder="Teljes név"
                  className="mt-1.5 border-[#E2E8F0] focus:border-[#0B1B3D] focus:ring-[#0B1B3D]"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-[#1A1F2C] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="pelda@email.hu"
                className="mt-1.5 border-[#E2E8F0] focus:border-[#0B1B3D] focus:ring-[#0B1B3D]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#1A1F2C] font-medium">Jelszó</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="Jelszó"
                  className="border-[#E2E8F0] focus:border-[#0B1B3D] focus:ring-[#0B1B3D] pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0B1B3D]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#0B1B3D] hover:bg-[#162B56] text-white font-semibold"
              size="lg"
            >
              {loading ? 'Kérjük várjon...' : isRegister ? 'Regisztráció' : 'Bejelentkezés'}
              {isRegister ? <UserPlus className="ml-2" size={16} /> : <LogIn className="ml-2" size={16} />}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-sm text-[#64748B] hover:text-[#0B1B3D] transition-colors"
            >
              {isRegister ? 'Már van fiókja? Jelentkezzen be' : 'Még nincs fiókja? Regisztráljon'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
